import type { NostrEvent } from '@nostrify/nostrify';
import { ArticleCard } from '@/components/ArticleCard';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';

interface ArticleGridProps {
  articles: NostrEvent[] | undefined;
  isLoading?: boolean;
  /** Number of skeleton cards to show while loading. */
  skeletonCount?: number;
  /** Shown in a dashed card when there's nothing to display. */
  emptyMessage: string;
}

/**
 * The standard responsive grid of `ArticleCard`s, with matching loading
 * skeletons and an empty state.
 *
 * Extracted because the identical grid + skeleton + empty-state markup
 * was previously repeated across the homepage, the starred leaderboard,
 * the profile page and search results — four copies that had already
 * drifted apart in skeleton shape and column counts.
 */
export function ArticleGrid({ articles, isLoading, skeletonCount = 6, emptyMessage }: ArticleGridProps) {
  if (isLoading) {
    return (
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {Array.from({ length: skeletonCount }).map((_, i) => (
          <Card key={i}>
            <CardContent className="space-y-3 pt-6">
              <Skeleton className="h-5 w-3/4" />
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-5/6" />
              <div className="flex items-center gap-2 pt-2">
                <Skeleton className="size-5 rounded-full" />
                <Skeleton className="h-3 w-20" />
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  if (!articles || articles.length === 0) {
    return (
      <Card className="border-dashed">
        <CardContent className="py-12 px-8 text-center">
          <p className="mx-auto max-w-sm text-muted-foreground">{emptyMessage}</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
      {articles.map((event) => (
        <ArticleCard key={event.id} event={event} />
      ))}
    </div>
  );
}
